import { Download } from 'lucide-react'
import { Button } from './Button'

interface Props {
  selectedCount: number
  /** Rows currently loaded in the table — the most an export can hold. */
  rowCount: number
  /** Plural noun for the rows, as `Pagination` takes one: "targets", "board entries". */
  noun: string
  onDownload: () => void
}

/**
 * Export a table as CSV.
 *
 * Its own component for the **label**, which is the part that matters. "Nothing
 * selected" means *every row*, which is a sensible default and an unpleasant
 * surprise: a reader who cleared their selection and pressed a button reading
 * "Download CSV" would get a file they did not ask for, and would have no way to
 * tell from the button that they had. So the count is in the label rather than
 * beside it, and one copy of that wording serves both tables so the two cannot
 * drift into describing different behaviour.
 *
 * "on this page" is literal, not hedging. Both tables page server-side and the CSV
 * is built from the rows already in memory, so an export can only ever hold the
 * page in front of the reader — a label promising the whole run would be wrong by
 * however many pages there are.
 */
export function DownloadCsvButton({
  selectedCount,
  rowCount,
  noun,
  onDownload,
}: Props) {
  // Says both what this does and what it does not. The same expanded row offers a
  // report that costs money and is confirmed in a modal, so a second download
  // affordance beside it has to make clear that this one calls nothing.
  const title =
    `Built in the browser from the ${noun} on this page. ` +
    'Nothing is sent to the server, and no report is commissioned.'

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onDownload}
      disabled={rowCount === 0}
      title={title}
    >
      <Download className="h-3.5 w-3.5" />
      {selectedCount === 0
        ? `Download CSV · all ${rowCount} on this page`
        : `Download CSV · ${selectedCount} selected`}
    </Button>
  )
}
